import {Component, Input, OnInit} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {GenericModalService} from '../../../../services/generic-modal.service';
import {moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {Utils} from '../../../../services/utils';
import {CustomValidator} from '../../../../services/custom-validator';
import {ExtractionService} from '../../../../services/configuration/extraction.service';

@Component({
  selector: 'app-extraction-layout-modal',
  templateUrl: './extraction-layout-modal.component.html',
  styleUrls: ['./extraction-layout-modal.component.css', '../../../../styles/modals.css']
})
export class ExtractionLayoutModalComponent implements OnInit {

  @Input() modalTitle: string;
  itemSaved: Subject<boolean> = new Subject();
  layoutForm: FormGroup;
  objInitialState: any;
  submitted = false;
  submitClick = false;
  errorMessage = '';
  businessProcesses$: Observable<any[]>;
  fields: any[] = [];
  selectedFields: any[] = [];
  fieldsDone = false;
  isNew: boolean;
  separators = [',', '.'];
  types = [{ description: 'Padrão', value: 0 },
            { description: 'Lote', value: 1 }];
  fileLayout: any;
  businessProcessList: any[] = [];
  pleaseWait = false;

  get formData() { return this.layoutForm.controls; }

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: GenericModalService,
    private formBuilder: FormBuilder,
    private extractionService: ExtractionService
  ) { }

  ngOnInit() {
    this.pleaseWait = true;
    this.isNew = this.objInitialState.businessProcessId === undefined;
    this.createForm();
    this.getBusinessProcesses();
    if (!this.isNew) {
      this.getDataExtractionFileLayoutById();
    }
  }

  createForm() {
    this.layoutForm = this.formBuilder.group({
      name: [ '', Validators.compose( [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern('^[^<>]+$')]) ],
      fileLayoutId: 0,
      description: [ '', Validators.maxLength(400) ],
      businessProcessId: [0, Validators.required ],
      type: [0, Validators.required ],
      positiveBoolean: '',
      negativeBoolean: '',
      dateFormat: '',
      decimalSeparator: ',',
      decimalPrecision: '',
      showFileHeader: false,
      fieldColumns: [
        []
      ]
    });
  }

  getDataExtractionFileLayoutById() {
      this.extractionService.getDataExtractionFileLayoutById(this.objInitialState.fileLayoutId)
        .subscribe( result => {
          this.fileLayout = result;
          this.layoutForm.patchValue(
            {
              businessProcessId: result.businessProcessId,
              name: result.name,
              fileLayoutId: result.fileLayoutId,
              description: result.description,
              type: result.type,
              positiveBoolean: result.positiveBoolean,
              negativeBoolean: result.negativeBoolean,
              dateFormat: result.dateFormat,
              decimalSeparator: result.decimalSeparator,
              decimalPrecision: result.decimalPrecision,
              showFileHeader: result.showFileHeader,
            }
          );
          if (result.type == 1) {
            this.onChangeType(1);
          }
          this.pleaseWait = false;
        }, error1 => this.errorMessage = error1);
  }

  getBusinessProcesses() {
    this.businessProcesses$ = this.extractionService.getBusinessProcessesForDataExtraction();
    this.businessProcesses$.subscribe(
      result => {
        this.businessProcessList = result;
        if (result.length > 0 && this.isNew) {
          this.layoutForm.patchValue(
            { businessProcessId: result[0].businessProcessId }
          );
          this.getBusinessProcessFields(result[0]);
        }
        if (!this.isNew) {
          const currentBusinessProcess = result.find( item => item.businessProcessId === this.objInitialState.businessProcessId);
          this.getBusinessProcessFields(currentBusinessProcess);
        } else {
          this.pleaseWait = false;
        }
      }, error1 => this.errorMessage = error1);
  }

  onChangeBusinessProcess(businessProcessId) {
    const businessProcessObject = this.businessProcessList.find(bp => bp.businessProcessId == businessProcessId);
    this.getBusinessProcessFields(businessProcessObject);
  }

  getBusinessProcessFields(businessProcess) {
    this.errorMessage = '';
    this.fieldsDone = false;
    this.fields = [];
    this.selectedFields = [];
    this.extractionService.getFieldsByBusinessProcessSpecificVersion(businessProcess.businessProcessId, businessProcess.version)
      .subscribe( result => {
        this.fields = result;
        this.fieldsDone = true;
        if (!this.isNew && businessProcess.businessProcessId == this.objInitialState.businessProcessId) {
          this.organizeFieldLists();
        }
      }, error1 => { this.errorMessage = error1; });
  }

  onChangeType(type) {
    const affectedControls = ['positiveBoolean', 'negativeBoolean', 'dateFormat', 'decimalPrecision'];
    if (type == 1) {
      this.layoutForm.patchValue({
        showFileHeader: false,
        decimalSeparator: ','
      });
      affectedControls.forEach( control => {
        this.layoutForm.get(control).setValidators(Validators.required);
        if (control === 'decimalPrecision') { this.layoutForm.get(control).setValidators(
          [CustomValidator.numeric, Validators.min(1), Validators.max(8), Validators.required]
        ); }
        this.layoutForm.get(control).updateValueAndValidity();
      });
    } else {
        this.layoutForm.patchValue({
          positiveBoolean: '',
          negativeBoolean: '',
          dateFormat: '',
          showFileHeader: false,
          decimalPrecision: ''
        });
        affectedControls.forEach( control => {
          this.layoutForm.get(control).clearValidators();
          this.layoutForm.get(control).updateValueAndValidity();
        });
    }
  }

  onClose() {
    this.itemSaved.next(false);
    this.bsModalRef.hide();
  }

  organizeFieldLists() {
    this.selectedFields = [];
    this.fileLayout.fieldColumns.forEach( fieldColumn => {
      delete fieldColumn.extensionData;
    });

    const fieldsIds = this.fileLayout.fieldColumns.sort(
      (a, b) => { return a.fieldColumnOrder - b.fieldColumnOrder;
      }).map( f => f.fieldId);

    for (let i = 0; i < this.fileLayout.fieldColumns.length; i++) {
      if (fieldsIds.includes(this.fileLayout.fieldColumns[i].fieldId)) {
        this.selectedFields[i] = this.fields
          .find( f => f.fieldId === this.fileLayout.fieldColumns[i].fieldId);
      }
    }

    this.selectedFields.forEach( sf => {
      this.fields.splice(this.fields.indexOf(sf), 1);
    });
  }

  onSelectAll() {
    for (let i = 0; this.fields.length !== 0; i++) {
      transferArrayItem(this.fields, this.selectedFields, 0, this.selectedFields.length);
    }
  }

  onDeselectAll() {
    for (let i = 0; this.selectedFields.length !== 0; i++) {
      transferArrayItem(this.selectedFields, this.fields, 0, this.fields.length);
    }
  }

  drop(event) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  onSave() {
    this.submitClick = true;
    this.errorMessage = '';
    const layout = this.layoutForm.getRawValue();
    layout.name = layout.name.trim();
    layout.type = Number(layout.type);
    if (layout.type !== 1) {
      layout.decimalPrecision = null;
      layout.positiveBoolean = null;
      layout.negativeBoolean = null;
      layout.dateFormat = null;
      layout.showFileHeader = null;
      layout.decimalSeparator = null;
    }
    const array = [];
    let fileLayoutId;
    this.isNew ? fileLayoutId = 0 : fileLayoutId = this.fileLayout.fileLayoutId;
    this.selectedFields.forEach( field => {
      array.push({
        fileLayoutId: fileLayoutId,
        businessProcessId: layout.businessProcessId,
        fieldColumnOrder: this.selectedFields.indexOf(field),
        fieldId: field.fieldId
      });
    });
    layout.fieldColumns = array;
    if (layout.dateFormats) {
      layout.dateFormats = layout.dateFormats.split('\n');
    }
    if (this.isNew) {
      this.createLayout(layout);
    } else {
      this.editLayout(layout);
    }
  }

  createLayout(layout: any) {
    this.errorMessage = '';
    this.extractionService.createDataExtractionFileLayout(layout)
      .subscribe( newLayoutId => {
        this.submitted = true;
        this.itemSaved.next(true);
        setTimeout(() => { this.submitted = false; this.bsModalRef.hide(); }, 2000);
      }, error1 => {
        let err = error1;
        if (error1.indexOf('401') !== -1) {
          err = 'Erro: Sessão expirada.';
        }
        this.errorMessage = err;
        this.submitClick = false;
      });
  }

  editLayout(layout: any) {
    this.errorMessage = '';
    this.extractionService.updateDataExtractionFileLayout(layout)
      .subscribe( () => {
        this.submitted = true;
        this.itemSaved.next(true);
        setTimeout(() => { this.submitted = false; this.bsModalRef.hide(); }, 2000);
      }, error1 => {
        let err = error1;
        if (error1.indexOf('401') !== -1) {
          err = 'Erro: Sessão expirada.';
        }
        this.errorMessage = err;
        this.submitClick = false;
      });
  }

  adjustSpaces(value, formControlName) {
    Utils.adjustSpaces(value, this.layoutForm, formControlName);
  }

}
